import { useCallback, useEffect, useRef, useState } from 'react'
import { httpClient } from '../../shared/api/httpClient'
import { useAstreaEnvironment } from '../../shared/hooks/useAstreaEnvironment'
import { getEntityTypeName } from './actionAuditConstants'

async function fetchAuditPage({ baseUrl, params, cursor, signal }) {
  const response = await httpClient.post(
    `${baseUrl}/api/v2/logs/query`,
    { ...params, cursor },
    { signal },
  )
  const { cursor: nextCursor, logs = [] } = response.data

  return {
    nextCursor: nextCursor || '',
    logs: logs.map((log) => ({ ...log, entityType: getEntityTypeName(log.entityType) })),
  }
}

export function useActionAuditQuery() {
  const { urlAstreaPath } = useAstreaEnvironment()

  const [audits, setAudits] = useState([])
  const [cursor, setCursor] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  const filtersRef = useRef(null)
  const baseUrlRef = useRef('')
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef(null)

  const hasMore = Boolean(cursor)
  const isError = error !== null

  // Aborts whatever request is still in flight when the component using this
  // hook unmounts — otherwise a search()/loadMore() the user navigated away
  // from would keep running against the Astrea backend for nothing.
  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  // Tags each fetch with a request id so a response that resolves after a
  // newer search()/loadMore() call has already started is silently dropped
  // instead of clobbering the newer state (e.g. a slow "Carregar mais" page
  // resolving after the user already fired a new "Filtrar" search). Also
  // aborts whichever request that supersedes, so a superseded search/page
  // stops consuming backend/network resources instead of running to
  // completion just to be discarded client-side.
  const runFetch = useCallback(async (pageCursor, setLoadingFlag, applyResult) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    const requestId = ++requestIdRef.current
    setError(null)
    setLoadingFlag(true)

    try {
      const result = await fetchAuditPage({
        baseUrl: baseUrlRef.current,
        params: filtersRef.current,
        cursor: pageCursor,
        signal: controller.signal,
      })
      if (requestId !== requestIdRef.current) return
      applyResult(result)
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      setError(err)
    } finally {
      if (requestId === requestIdRef.current) setLoadingFlag(false)
    }
  }, [])

  const search = useCallback(
    (filters) => {
      filtersRef.current = filters
      baseUrlRef.current = urlAstreaPath
      setAudits([])
      setCursor('')
      setIsLoadingMore(false)

      return runFetch('', setIsLoading, ({ nextCursor, logs }) => {
        setAudits(logs)
        setCursor(nextCursor)
      })
    },
    [urlAstreaPath, runFetch],
  )

  const loadMore = useCallback(() => {
    if (!cursor || isLoadingMore) return

    return runFetch(cursor, setIsLoadingMore, ({ nextCursor, logs }) => {
      setAudits((current) => [...current, ...logs])
      setCursor(nextCursor)
    })
  }, [cursor, isLoadingMore, runFetch])

  return {
    audits,
    isLoading,
    isLoadingMore,
    isError,
    error,
    hasMore,
    search,
    loadMore,
  }
}

export default useActionAuditQuery
